/**
 * THE STATE MACHINES.
 *
 * Direct transcription of docs/phase0/05-state-machines.md. Keep the two in
 * sync; `machines.test.ts` asserts every n² state pair is either declared legal
 * or rejected, so an undeclared transition can never quietly become reachable.
 */

import type { StateMachine } from './types.js';

// ---------------------------------------------------------------------------
// 1. Project lifecycle stage
// ---------------------------------------------------------------------------
export const STAGE_KEYS = [
  'award',
  'design',
  'approvals',
  'procurement',
  'site_execution',
  'qa_qc',
  'snagging',
  'handover',
] as const;
export type StageKey = (typeof STAGE_KEYS)[number];

export type StageStatus = 'not_started' | 'active' | 'at_risk' | 'blocked' | 'complete';

export const stageMachine: StateMachine<StageStatus> = {
  name: 'project_stage',
  states: ['not_started', 'active', 'at_risk', 'blocked', 'complete'],
  initial: 'not_started',
  // `complete` is re-openable by PM+ with a reason, so it is not terminal.
  terminal: [],
  transitions: [
    {
      from: 'not_started',
      to: 'active',
      /*
       * `system` ADDED 2026-09-01 (ADR-043), and it is the same shape of change
       * `at_risk` was: the app may now start a stage when work demonstrably
       * begins on it — the first submittal starts Approvals, the first material
       * starts Procurement. `projects/stage-autostart.ts` is the whole of it.
       *
       * The user asked for it after meeting the gap: a project reading Critical
       * over a material seventeen days past its order-by date, whose "Needs you
       * now" list talked about a drawing, because that panel reads the gates of
       * RUNNING stages and nobody had pressed Start.
       *
       * A person can still start a stage, and normally does. Nothing was taken
       * away here; a third actor was added.
       */
      by: ['project_manager', 'company_admin', 'system'],
      auditAction: 'stage.started',
      note: 'Previous stage need not be complete — stages overlap (ADR-002).',
    },
    { from: 'active', to: 'at_risk', by: ['system'], auditAction: 'stage.at_risk' },
    { from: 'at_risk', to: 'active', by: ['system'], auditAction: 'stage.risk_cleared' },
    {
      from: 'active',
      to: 'blocked',
      by: ['system', 'project_manager'],
      auditAction: 'stage.blocked',
    },
    { from: 'at_risk', to: 'blocked', by: ['system'], auditAction: 'stage.blocked' },
    { from: 'blocked', to: 'active', by: ['system'], auditAction: 'stage.unblocked' },
    { from: 'blocked', to: 'at_risk', by: ['system'], auditAction: 'stage.risk_detected' },
    {
      from: 'active',
      to: 'complete',
      by: ['project_manager', 'company_admin'],
      auditAction: 'stage.completed',
      note: 'Requires all mandatory gate items satisfied, or an override.',
    },
    {
      from: 'at_risk',
      to: 'complete',
      by: ['project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'stage.overridden',
    },
    {
      from: 'blocked',
      to: 'complete',
      by: ['project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'stage.overridden',
    },
    {
      from: 'complete',
      to: 'active',
      by: ['project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'stage.reopened',
    },
  ],
};

// ---------------------------------------------------------------------------
// 2. Submittal — the most contractually significant machine in the product
// ---------------------------------------------------------------------------
export type SubmittalStatus =
  | 'draft'
  | 'internal_review'
  | 'submitted'
  | 'approved'
  | 'approved_with_comments'
  | 'rejected'
  | 'resubmit_required'
  | 'void';

export const submittalMachine: StateMachine<SubmittalStatus> = {
  name: 'submittal',
  states: [
    'draft',
    'internal_review',
    'submitted',
    'approved',
    'approved_with_comments',
    'rejected',
    'resubmit_required',
    'void',
  ],
  initial: 'draft',
  terminal: ['approved', 'approved_with_comments', 'void'],
  transitions: [
    {
      from: 'draft',
      to: 'internal_review',
      by: ['design_coordinator', 'project_manager', 'company_admin'],
      auditAction: 'submittal.internal_review_started',
    },
    {
      from: 'internal_review',
      to: 'draft',
      by: ['design_coordinator', 'project_manager', 'company_admin'],
      auditAction: 'submittal.returned_to_draft',
    },
    {
      from: 'internal_review',
      to: 'submitted',
      by: ['design_coordinator', 'project_manager', 'company_admin'],
      auditAction: 'submittal.submitted',
      note: 'Starts the SLA clock against required_response_date.',
    },
    /*
     * WHO MAY DECIDE A SUBMITTAL — widened 2026-08-27, by the user's decision.
     *
     * It was `client_consultant` and `director` only, and the reasoning was
     * good: a submittal is an approval from OUTSIDE, and one the contractor can
     * grant itself is not an approval. Day 44b left it deliberately untouched
     * when the same question was asked about variations.
     *
     * What that reasoning missed is the job it was blocking. On most fit-out
     * contracts the consultant does NOT have a login — they answer by email,
     * and somebody inside has to record what they said. The Director could
     * already do that; the Project Manager, whose register this is, could not,
     * and was refused every decision on a submittal they had raised themselves.
     *
     * `04` ALREADY GRANTS THE PM `submittal: approve` (`g('VCEAX')`). So the PM
     * half is not a widening of the permission model at all — it is the machine
     * catching up with the matrix it had drifted from. That mismatch is exactly
     * the class `scripts/qa/machine-vs-matrix.ts` exists to catch, and it did
     * not catch this one because it checks one direction only: every role the
     * MACHINE names can reach the endpoint. A role the MATRIX grants that the
     * machine never names is invisible to it.
     *
     * `company_admin` is here for a different and weaker reason, and it is
     * marked so it can be found again: the user asked for it **to test with**,
     * intending to take it away later — *"the admin as well for testing and
     * then remove the acces later"*. Deleting `'company_admin'` from these four
     * lines is the whole of that removal. Nothing else refers to it.
     *
     * WHAT DID NOT CHANGE: the record still says who decided. `approval_action`
     * is append-only and stores the actor, so a submittal approved internally
     * stays distinguishable forever from one the consultant answered. The rule
     * moved; the evidence did not.
     */
    {
      from: 'submitted',
      to: 'approved',
      by: ['client_consultant', 'director', 'project_manager', 'company_admin'],
      auditAction: 'submittal.approved',
      note: 'Releases every blocks-linked material (propagation rule P1).',
    },
    {
      from: 'submitted',
      to: 'approved_with_comments',
      by: ['client_consultant', 'director', 'project_manager', 'company_admin'],
      auditAction: 'submittal.approved_with_comments',
    },
    {
      from: 'submitted',
      to: 'rejected',
      by: ['client_consultant', 'director', 'project_manager', 'company_admin'],
      auditAction: 'submittal.rejected',
    },
    {
      from: 'submitted',
      to: 'resubmit_required',
      by: ['client_consultant', 'director', 'project_manager', 'company_admin'],
      auditAction: 'submittal.resubmit_requested',
    },
    {
      from: 'rejected',
      to: 'draft',
      by: ['design_coordinator', 'company_admin'],
      auditAction: 'submittal.revised',
    },
    {
      from: 'resubmit_required',
      to: 'draft',
      by: ['design_coordinator', 'company_admin'],
      auditAction: 'submittal.revised',
    },
    {
      from: 'draft',
      to: 'void',
      by: ['company_admin'],
      requiresReason: true,
      auditAction: 'submittal.voided',
    },
    {
      from: 'internal_review',
      to: 'void',
      by: ['company_admin'],
      requiresReason: true,
      auditAction: 'submittal.voided',
    },
    {
      from: 'submitted',
      to: 'void',
      by: ['company_admin'],
      requiresReason: true,
      auditAction: 'submittal.voided',
    },
  ],
};

// ---------------------------------------------------------------------------
// 3. Material item
// ---------------------------------------------------------------------------
export type MaterialStatus =
  | 'identified'
  | 'blocked_pending_approval'
  | 'ready_to_order'
  | 'ordered'
  | 'in_production'
  | 'shipped'
  | 'at_site'
  | 'inspected'
  | 'rejected'
  | 'installed'
  | 'cancelled';

const PROCUREMENT_ROLES = ['procurement', 'project_manager', 'company_admin'] as const;

export const materialMachine: StateMachine<MaterialStatus> = {
  name: 'material_item',
  states: [
    'identified',
    'blocked_pending_approval',
    'ready_to_order',
    'ordered',
    'in_production',
    'shipped',
    'at_site',
    'inspected',
    'rejected',
    'installed',
    'cancelled',
  ],
  initial: 'identified',
  terminal: ['installed', 'cancelled'],
  transitions: [
    {
      from: 'identified',
      to: 'blocked_pending_approval',
      by: ['system'],
      auditAction: 'material.blocked',
      note: 'Set automatically when linked to an unapproved submittal.',
    },
    {
      from: 'identified',
      to: 'ready_to_order',
      by: [...PROCUREMENT_ROLES],
      auditAction: 'material.ready',
    },
    {
      from: 'blocked_pending_approval',
      to: 'ready_to_order',
      by: ['system'],
      auditAction: 'material.released_by_approval',
      note: 'Propagation rule P1 — linked submittal approved.',
    },
    {
      from: 'blocked_pending_approval',
      to: 'ready_to_order',
      by: [...PROCUREMENT_ROLES],
      requiresReason: true,
      auditAction: 'material.exception_recorded',
      note: 'The proceed-at-risk path (ADR-003). Flagged permanently, shown in Pulse.',
    },
    { from: 'ready_to_order', to: 'ordered', by: [...PROCUREMENT_ROLES], auditAction: 'material.ordered' },
    {
      from: 'ordered',
      to: 'in_production',
      by: [...PROCUREMENT_ROLES],
      auditAction: 'material.in_production',
    },
    { from: 'ordered', to: 'shipped', by: [...PROCUREMENT_ROLES], auditAction: 'material.shipped' },
    {
      from: 'in_production',
      to: 'shipped',
      by: [...PROCUREMENT_ROLES],
      auditAction: 'material.shipped',
    },
    {
      from: 'shipped',
      to: 'at_site',
      by: ['site_engineer', ...PROCUREMENT_ROLES],
      auditAction: 'material.at_site',
      note: 'Creates or links a Delivery record with photos and quantity.',
    },
    { from: 'at_site', to: 'inspected', by: ['qa_qc'], auditAction: 'material.inspected' },
    { from: 'at_site', to: 'rejected', by: ['qa_qc'], auditAction: 'material.rejected' },
    { from: 'inspected', to: 'installed', by: ['site_engineer', 'project_manager', 'company_admin'], auditAction: 'material.installed' },
    { from: 'inspected', to: 'rejected', by: ['qa_qc'], auditAction: 'material.rejected' },
    {
      from: 'rejected',
      to: 'ordered',
      by: [...PROCUREMENT_ROLES],
      auditAction: 'material.reordered',
    },
    // Cancellation is available from every non-terminal state.
    ...(
      [
        'identified',
        'blocked_pending_approval',
        'ready_to_order',
        'ordered',
        'in_production',
        'shipped',
        'at_site',
        'inspected',
        'rejected',
      ] as const
    ).map((from) => ({
      from,
      to: 'cancelled' as const,
      by: [...PROCUREMENT_ROLES],
      requiresReason: true,
      auditAction: 'material.cancelled',
    })),
  ],
};

// ---------------------------------------------------------------------------
// 4. Inspection
// ---------------------------------------------------------------------------
export type InspectionStatus =
  | 'requested'
  | 'scheduled'
  | 'in_progress'
  | 'passed'
  | 'passed_with_observations'
  | 'failed'
  | 'cancelled';

export const inspectionMachine: StateMachine<InspectionStatus> = {
  name: 'inspection',
  states: [
    'requested',
    'scheduled',
    'in_progress',
    'passed',
    'passed_with_observations',
    'failed',
    'cancelled',
  ],
  initial: 'requested',
  terminal: ['passed', 'passed_with_observations', 'failed', 'cancelled'],
  transitions: [
    { from: 'requested', to: 'scheduled', by: ['qa_qc'], auditAction: 'inspection.scheduled' },
    { from: 'scheduled', to: 'in_progress', by: ['qa_qc'], auditAction: 'inspection.started' },
    { from: 'in_progress', to: 'passed', by: ['qa_qc'], auditAction: 'inspection.passed' },
    {
      from: 'in_progress',
      to: 'passed_with_observations',
      by: ['qa_qc'],
      auditAction: 'inspection.passed_with_observations',
      note: 'Observations become tasks, not NCRs.',
    },
    {
      from: 'in_progress',
      to: 'failed',
      by: ['qa_qc'],
      auditAction: 'inspection.failed',
      note: 'An NCR MUST be created in the same transaction — enforced at the API layer.',
    },
    {
      from: 'requested',
      to: 'cancelled',
      by: ['qa_qc', 'project_manager', 'site_engineer'],
      auditAction: 'inspection.cancelled',
    },
    {
      from: 'scheduled',
      to: 'cancelled',
      by: ['qa_qc', 'project_manager'],
      auditAction: 'inspection.cancelled',
    },
  ],
};

// ---------------------------------------------------------------------------
// 5. NCR
// ---------------------------------------------------------------------------
export type NcrStatus =
  | 'open'
  | 'corrective_action_assigned'
  | 'work_complete'
  | 'verified'
  | 'void';

export const ncrMachine: StateMachine<NcrStatus> = {
  name: 'ncr',
  states: ['open', 'corrective_action_assigned', 'work_complete', 'verified', 'void'],
  initial: 'open',
  terminal: ['verified', 'void'],
  transitions: [
    {
      from: 'open',
      to: 'corrective_action_assigned',
      by: ['qa_qc'],
      auditAction: 'ncr.assigned',
      note: 'Must name responsible party, corrective action and due date.',
    },
    {
      from: 'corrective_action_assigned',
      to: 'work_complete',
      by: [
        'site_engineer',
        'subcontractor',
        'project_manager',
        'design_coordinator',
        'procurement',
        'company_admin',
      ],
      requiresEvidence: true,
      auditAction: 'ncr.work_completed',
    },
    {
      from: 'work_complete',
      to: 'verified',
      by: ['qa_qc'],
      auditAction: 'ncr.verified',
      note: 'QA/QC only. The assignee cannot verify their own work (ADR-020).',
    },
    {
      from: 'work_complete',
      to: 'corrective_action_assigned',
      by: ['qa_qc'],
      requiresReason: true,
      auditAction: 'ncr.rejected_by_verifier',
    },
    { from: 'open', to: 'void', by: ['qa_qc'], requiresReason: true, auditAction: 'ncr.voided' },
    {
      from: 'corrective_action_assigned',
      to: 'void',
      by: ['qa_qc'],
      requiresReason: true,
      auditAction: 'ncr.voided',
    },
  ],
};

// ---------------------------------------------------------------------------
// 6. Snag
// ---------------------------------------------------------------------------
export type SnagStatus =
  | 'open'
  | 'assigned'
  | 'in_progress'
  | 'closure_submitted'
  | 'verified_closed'
  | 'accepted_as_is'
  | 'void';

const SNAG_DOER_ROLES = [
  'site_engineer',
  'subcontractor',
  'project_manager',
  'company_admin',
] as const;

export const snagMachine: StateMachine<SnagStatus> = {
  name: 'snag',
  states: [
    'open',
    'assigned',
    'in_progress',
    'closure_submitted',
    'verified_closed',
    'accepted_as_is',
    'void',
  ],
  initial: 'open',
  terminal: ['verified_closed', 'accepted_as_is', 'void'],
  transitions: [
    {
      from: 'open',
      to: 'assigned',
      by: ['site_engineer', 'qa_qc', 'project_manager', 'company_admin'],
      auditAction: 'snag.assigned',
    },
    { from: 'assigned', to: 'in_progress', by: [...SNAG_DOER_ROLES], auditAction: 'snag.started' },
    {
      from: 'in_progress',
      to: 'closure_submitted',
      by: [...SNAG_DOER_ROLES],
      requiresEvidence: true,
      auditAction: 'snag.closure_submitted',
      note: 'After-photo required for severity high and critical (configurable).',
    },
    {
      from: 'closure_submitted',
      to: 'verified_closed',
      by: ['qa_qc', 'project_manager', 'company_admin'],
      auditAction: 'snag.verified_closed',
      note: 'Never by the assignee (ADR-020).',
    },
    {
      from: 'closure_submitted',
      to: 'assigned',
      by: ['qa_qc', 'project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'snag.closure_rejected',
    },
    {
      from: 'open',
      to: 'accepted_as_is',
      by: ['client_consultant', 'project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'snag.accepted_as_is',
      note: 'The honest state for "the client agreed to live with it".',
    },
    {
      from: 'assigned',
      to: 'accepted_as_is',
      by: ['client_consultant', 'project_manager', 'company_admin'],
      requiresReason: true,
      auditAction: 'snag.accepted_as_is',
    },
    ...(['open', 'assigned', 'in_progress', 'closure_submitted'] as const).map((from) => ({
      from,
      to: 'void' as const,
      by: ['project_manager', 'company_admin'] as const,
      requiresReason: true,
      auditAction: 'snag.voided',
    })),
  ],
};

// ---------------------------------------------------------------------------
// 7. Variation
// ---------------------------------------------------------------------------
export type VariationStatus =
  | 'potential_change'
  | 'under_review'
  | 'quotation_submitted'
  | 'approved'
  | 'rejected'
  | 'instructed'
  | 'executed'
  | 'billed'
  | 'collected'
  | 'withdrawn';

const QS_ROLES = ['qs_commercial', 'company_admin'] as const;

export const variationMachine: StateMachine<VariationStatus> = {
  name: 'variation',
  states: [
    'potential_change',
    'under_review',
    'quotation_submitted',
    'approved',
    'rejected',
    'instructed',
    'executed',
    'billed',
    'collected',
    'withdrawn',
  ],
  initial: 'potential_change',
  terminal: ['collected', 'rejected', 'withdrawn'],
  transitions: [
    {
      from: 'potential_change',
      to: 'under_review',
      by: [...QS_ROLES],
      auditAction: 'variation.under_review',
    },
    {
      from: 'under_review',
      to: 'quotation_submitted',
      by: [...QS_ROLES],
      auditAction: 'variation.quoted',
    },
    /*
     * THE CLIENT DOES NOT PRESS THESE. User's decision, 2026-08-17: *"no need
     * for client approval from the app"*.
     *
     * The decision still belongs to the client — it just arrives by email or a
     * signed instruction, and the QS RECORDS it here, the same way a verbal
     * instruction on site is recorded. So these two moves are the commercial
     * team's, and `client_consultant` holds `variation: V` in `04` to match.
     *
     * Removing them from `by` is what stops the buttons appearing: the screen
     * renders `allowed_next`, so a role the machine does not name is offered
     * nothing. Before this, the machine offered a client Approve and Reject
     * and the route — guarded by `variation:edit` — answered 403.
     */
    {
      from: 'quotation_submitted',
      to: 'approved',
      by: ['director', ...QS_ROLES],
      auditAction: 'variation.approved',
      note: 'Adds to revised contract value; recomputes forecast margin (P9).',
    },
    {
      from: 'quotation_submitted',
      to: 'rejected',
      by: ['director', ...QS_ROLES],
      auditAction: 'variation.rejected',
    },
    {
      from: 'approved',
      to: 'instructed',
      by: ['project_manager', ...QS_ROLES],
      auditAction: 'variation.instructed',
    },
    {
      from: 'quotation_submitted',
      to: 'instructed',
      by: ['project_manager', ...QS_ROLES],
      auditAction: 'variation.instructed_without_approval',
      note: 'Clients say "just do it" before the paperwork. Recording that honestly is the point — it feeds unapproved exposure (ADR-022).',
    },
    {
      from: 'instructed',
      to: 'executed',
      by: ['project_manager', 'site_engineer', ...QS_ROLES],
      auditAction: 'variation.executed',
    },
    { from: 'executed', to: 'billed', by: [...QS_ROLES], auditAction: 'variation.billed' },
    { from: 'billed', to: 'collected', by: [...QS_ROLES], auditAction: 'variation.collected' },
    ...(
      ['potential_change', 'under_review', 'quotation_submitted'] as const
    ).map((from) => ({
      from,
      to: 'withdrawn' as const,
      by: ['project_manager', ...QS_ROLES] as const,
      requiresReason: true,
      auditAction: 'variation.withdrawn',
    })),
  ],
};

// ---------------------------------------------------------------------------
// 8. Task
// ---------------------------------------------------------------------------
export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';

const TASK_ACTOR_ROLES = [
  'company_admin',
  'director',
  'project_manager',
  'design_coordinator',
  'procurement',
  'site_engineer',
  'qs_commercial',
  'qa_qc',
  'subcontractor',
] as const;

export const taskMachine: StateMachine<TaskStatus> = {
  name: 'task',
  states: ['todo', 'in_progress', 'blocked', 'done', 'cancelled'],
  initial: 'todo',
  terminal: ['done', 'cancelled'],
  transitions: [
    { from: 'todo', to: 'in_progress', by: [...TASK_ACTOR_ROLES], auditAction: 'task.started' },
    { from: 'in_progress', to: 'todo', by: [...TASK_ACTOR_ROLES], auditAction: 'task.reset' },
    {
      from: 'todo',
      to: 'blocked',
      by: ['system', ...TASK_ACTOR_ROLES],
      auditAction: 'task.blocked',
      note: 'Auto-entered when a blocks-linked cause is unresolved (P4).',
    },
    {
      from: 'in_progress',
      to: 'blocked',
      by: ['system', ...TASK_ACTOR_ROLES],
      auditAction: 'task.blocked',
    },
    { from: 'blocked', to: 'todo', by: ['system', ...TASK_ACTOR_ROLES], auditAction: 'task.unblocked' },
    {
      from: 'blocked',
      to: 'in_progress',
      by: ['system', ...TASK_ACTOR_ROLES],
      auditAction: 'task.unblocked',
    },
    { from: 'in_progress', to: 'done', by: [...TASK_ACTOR_ROLES], auditAction: 'task.completed' },
    { from: 'todo', to: 'done', by: [...TASK_ACTOR_ROLES], auditAction: 'task.completed' },
    ...(['todo', 'in_progress', 'blocked'] as const).map((from) => ({
      from,
      to: 'cancelled' as const,
      by: ['project_manager', 'company_admin'] as const,
      auditAction: 'task.cancelled',
    })),
  ],
};

// ---------------------------------------------------------------------------
// 9. Report
// ---------------------------------------------------------------------------
export type ReportStatus = 'scheduled' | 'generating' | 'draft' | 'issued' | 'failed' | 'discarded';

export const reportMachine: StateMachine<ReportStatus> = {
  name: 'report',
  states: ['scheduled', 'generating', 'draft', 'issued', 'failed', 'discarded'],
  initial: 'scheduled',
  // `issued` is terminal for the report record; reissuing appends a new
  // immutable report_issue version rather than mutating this row.
  terminal: ['issued', 'discarded'],
  transitions: [
    { from: 'scheduled', to: 'generating', by: ['system'], auditAction: 'report.generation_started' },
    { from: 'generating', to: 'draft', by: ['system'], auditAction: 'report.drafted' },
    { from: 'generating', to: 'failed', by: ['system'], auditAction: 'report.generation_failed' },
    { from: 'failed', to: 'generating', by: ['system', 'project_manager'], auditAction: 'report.retried' },
    {
      from: 'draft',
      to: 'issued',
      by: ['project_manager', 'company_admin', 'qs_commercial'],
      auditAction: 'report.issued',
    },
    {
      from: 'draft',
      to: 'discarded',
      by: ['project_manager', 'company_admin'],
      auditAction: 'report.discarded',
    },
  ],
};

// ---------------------------------------------------------------------------
// 10. Deliverable (a drawing, a schedule, a specification)
//
// `05` §9:
//
//   planned ──► in_progress ──► issued ──► superseded ⬛
//                     │            │
//                     └────────────┴──► cancelled ⬛
//
// `superseded` is reached by ISSUING A NEW REVISION, never by a person choosing
// it from a menu — a drawing does not become out of date because somebody said
// so, it becomes out of date because a newer one exists. That is why the
// transition belongs to `system`.
//
// `cancelled` is the Design gate's "explicitly waived": a drawing that turned
// out not to be needed. It demands a reason, because the gate stops asking
// about it afterwards and somebody has to have said why.
// ---------------------------------------------------------------------------
export type DeliverableStatus =
  | 'planned'
  | 'in_progress'
  | 'issued'
  | 'superseded'
  | 'cancelled';

const DELIVERABLE_ACTOR_ROLES = [
  'design_coordinator',
  'project_manager',
  'company_admin',
] as const;

export const deliverableMachine: StateMachine<DeliverableStatus> = {
  name: 'deliverable',
  states: ['planned', 'in_progress', 'issued', 'superseded', 'cancelled'],
  initial: 'planned',
  terminal: ['superseded', 'cancelled'],
  transitions: [
    {
      from: 'planned',
      to: 'in_progress',
      by: [...DELIVERABLE_ACTOR_ROLES],
      auditAction: 'deliverable.started',
    },
    {
      from: 'in_progress',
      to: 'planned',
      by: [...DELIVERABLE_ACTOR_ROLES],
      auditAction: 'deliverable.reset',
    },
    {
      from: 'in_progress',
      to: 'issued',
      by: [...DELIVERABLE_ACTOR_ROLES],
      requiresEvidence: true,
      auditAction: 'deliverable.issued',
      note: 'Issuing means a revision exists. "Issued" with nothing attached is a claim, not a drawing.',
    },
    {
      from: 'planned',
      to: 'issued',
      by: [...DELIVERABLE_ACTOR_ROLES],
      requiresEvidence: true,
      auditAction: 'deliverable.issued',
      note: 'Small jobs go straight from planned to issued; forcing a tick through in_progress would only teach people to tick it.',
    },
    {
      from: 'issued',
      to: 'superseded',
      by: ['system'],
      auditAction: 'deliverable.superseded',
      note: 'Reached by issuing a newer revision, never chosen by hand.',
    },
    ...(['planned', 'in_progress', 'issued'] as const).map((from) => ({
      from,
      to: 'cancelled' as const,
      by: ['project_manager', 'company_admin', 'design_coordinator'] as const,
      requiresReason: true,
      auditAction: 'deliverable.cancelled',
    })),
  ],
};

// ---------------------------------------------------------------------------

export const ALL_MACHINES = [
  stageMachine,
  submittalMachine,
  materialMachine,
  inspectionMachine,
  ncrMachine,
  snagMachine,
  variationMachine,
  taskMachine,
  reportMachine,
  deliverableMachine,
] as const;
